export interface SmtpIntegration {
  host: string;
  port: number;
  username: string;
  fromEmail: string;
  /** Optional Reply-To for AR tracking (e.g. accounts@yourdomain.com) */
  replyToEmail: string;
  useTls: boolean;
  password: string;
}

export interface XeroCollectionsSettings {
  attachPdf: boolean;
  stripePaymentLink: boolean;
  /** Reply-To alias; if empty, uses plus-tag on from address for tracking */
  replyToEmail: string;
}

export interface XeroIntegration {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  collections: XeroCollectionsSettings;
}

export interface StripeBillingSettings {
  autoSendInvoices: boolean;
  daysUntilDue: number;
  reminderEnabled: boolean;
  /** Days before due date to send reminders, e.g. [7, 3, 1] */
  reminderDaysBefore: number[];
  reminderChannel: 'email' | 'stripe' | 'both';
  defaultCurrency: string;
}

export interface StripeIntegration {
  publishableKey: string;
  secretKey: string;
  billing: StripeBillingSettings;
}

export interface DudaAccount {
  id: string;
  label: string;
  apiUser: string;
  apiPassword: string;
  siteName: string;
}

export interface DudaIntegration {
  accounts: DudaAccount[];
  activeAccountId: string;
}

export type MetaPlatform = 'facebook' | 'instagram' | 'both';

export interface MetaAccount {
  id: string;
  label: string;
  platform: MetaPlatform;
  pageId: string;
  instagramBusinessId: string;
  accessToken: string;
}

export interface MetaIntegration {
  appId: string;
  appSecret: string;
  activeAccountId: string;
  accounts: MetaAccount[];
}

export interface GoogleAdsIntegration {
  developerToken: string;
  clientId: string;
  clientSecret: string;
  customerId: string;
  refreshToken: string;
  loginCustomerId: string;
}

export interface SearchConsoleIntegration {
  siteUrl: string;
  serviceAccountJson: string;
}

export interface Ga4Integration {
  propertyId: string;
  measurementId: string;
  serviceAccountJson: string;
}

export interface MerchantCenterIntegration {
  merchantId: string;
  serviceAccountJson: string;
}

export interface SlackIntegration {
  workspaceName: string;
  channelId: string;
  botToken: string;
  signingSecret: string;
}

export interface TwilioIntegration {
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  messagingServiceSid: string;
}

export interface WizzardIntegration {
  apiKey: string;
  model: string;
  enabled: boolean;
}

export interface WorkspaceProfile {
  name: string;
}

export interface AppSettings {
  version: 2;
  workspace: WorkspaceProfile;
  integrations: {
    smtp: SmtpIntegration;
    stripe: StripeIntegration;
    xero: XeroIntegration;
    duda: DudaIntegration;
    meta: MetaIntegration;
    googleAds: GoogleAdsIntegration;
    searchConsole: SearchConsoleIntegration;
    ga4: Ga4Integration;
    merchantCenter: MerchantCenterIntegration;
    slack: SlackIntegration;
    twilio: TwilioIntegration;
    wizzard: WizzardIntegration;
  };
}

export type SettingsResponse = AppSettings & {
  configuredSecrets: string[];
  secretsStorage: 'secret-manager' | 'local';
};

export const defaultStripeBilling = (): StripeBillingSettings => ({
  autoSendInvoices: true,
  daysUntilDue: 14,
  reminderEnabled: true,
  reminderDaysBefore: [7, 3, 1],
  reminderChannel: 'both',
  defaultCurrency: 'aud',
});

export const defaultSettings = (): AppSettings => ({
  version: 2,
  workspace: { name: 'My workspace' },
  integrations: {
    smtp: {
      host: 'smtp.gmail.com',
      port: 587,
      username: '',
      fromEmail: '',
      replyToEmail: '',
      useTls: true,
      password: '',
    },
    stripe: { publishableKey: '', secretKey: '', billing: defaultStripeBilling() },
    xero: {
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      collections: { attachPdf: true, stripePaymentLink: true, replyToEmail: '' },
    },
    duda: {
      activeAccountId: 'duda-a1',
      accounts: [
        {
          id: 'duda-a1',
          label: 'Primary account',
          apiUser: '',
          apiPassword: '',
          siteName: '',
        },
      ],
    },
    meta: {
      appId: '',
      appSecret: '',
      activeAccountId: 'meta-a1',
      accounts: [
        {
          id: 'meta-a1',
          label: 'Primary page',
          platform: 'both',
          pageId: '',
          instagramBusinessId: '',
          accessToken: '',
        },
      ],
    },
    googleAds: {
      developerToken: '',
      clientId: '',
      clientSecret: '',
      customerId: '',
      refreshToken: '',
      loginCustomerId: '',
    },
    searchConsole: { siteUrl: '', serviceAccountJson: '' },
    ga4: { propertyId: '', measurementId: '', serviceAccountJson: '' },
    merchantCenter: { merchantId: '', serviceAccountJson: '' },
    slack: { workspaceName: '', channelId: '', botToken: '', signingSecret: '' },
    twilio: { accountSid: '', authToken: '', phoneNumber: '', messagingServiceSid: '' },
    wizzard: { apiKey: '', model: 'grok-3-mini', enabled: true },
  },
});

export function dudaPasswordSecretPath(accountId: string): string {
  return `integrations.duda.accounts.${accountId}.apiPassword`;
}

export function metaAccessTokenSecretPath(accountId: string): string {
  return `integrations.meta.accounts.${accountId}.accessToken`;
}

export const SECRET_FIELD_PATHS = {
  smtpPassword: 'integrations.smtp.password',
  stripeSecretKey: 'integrations.stripe.secretKey',
  xeroClientSecret: 'integrations.xero.clientSecret',
  metaAppSecret: 'integrations.meta.appSecret',
  googleAdsClientSecret: 'integrations.googleAds.clientSecret',
  googleAdsRefreshToken: 'integrations.googleAds.refreshToken',
  searchConsoleSa: 'integrations.searchConsole.serviceAccountJson',
  ga4Sa: 'integrations.ga4.serviceAccountJson',
  merchantCenterSa: 'integrations.merchantCenter.serviceAccountJson',
  slackBotToken: 'integrations.slack.botToken',
  slackSigningSecret: 'integrations.slack.signingSecret',
  twilioAuthToken: 'integrations.twilio.authToken',
  wizzardApiKey: 'integrations.wizzard.apiKey',
} as const;

/** Income reporting channel ids */
export type IncomeChannelId = 'stripe' | 'xero' | 'meta' | 'google_ads' | 'other';

export type ChannelIncomePoint = {
  date: string;
  stripePaid?: number;
  stripeInvoiced?: number;
  xeroPaid?: number;
  xeroInvoiced?: number;
  totalPaid?: number;
  totalInvoiced?: number;
  stripe: number;
  xero: number;
  meta: number;
  total: number;
};

export type ChannelIncomeSummary = {
  channel: IncomeChannelId;
  label: string;
  amount: number;
  invoicedAmount?: number;
  currency: string;
  count: number;
  trendPct: number | null;
};
